import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuthQuery, useLogoutMutation } from "../lib/api/auth";
import { useOverviewAttentionQuery, useProgrammesOverviewQuery } from "../lib/api/overview";
import { useReportingPeriod } from "../lib/hooks/useReportingPeriod";
import { breadcrumbForPath, navItemsForRole } from "../lib/navigation";
import { Sidebar } from "../components/layout/Sidebar";
import { RoleRouteGuard } from "../components/RoleRouteGuard";

export function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuthQuery();
  const logout = useLogoutMutation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const user = auth.data;

  useEffect(() => {
    document.querySelectorAll("dialog").forEach((element) => {
      if (element instanceof HTMLDialogElement) {
        element.close();
        element.remove();
      }
    });
  }, [location.pathname]);

  async function handleLogout() {
    await logout.mutateAsync();
    void navigate("/login", { replace: true });
  }

  const { period } = useReportingPeriod();
  const programmes = useProgrammesOverviewQuery(Boolean(user), period);
  const attention = useOverviewAttentionQuery(Boolean(user));
  const pendingCount = attention.data?.attention.pendingApprovals.count ?? 0;
  const hasScholarshipUnit =
    programmes.data?.groups.some((group) =>
      group.units.some((unit) => unit.code === "sch"),
    ) ?? false;

  const navItems = user
    ? navItemsForRole(user.role, user.unitIds)
        .filter((item) => item.to !== "/scholarships" || hasScholarshipUnit)
        .map((item) =>
          item.to === "/transactions" && pendingCount > 0
            ? { ...item, badge: pendingCount }
            : item,
        )
    : [];
  const breadcrumb = breadcrumbForPath(location.pathname);

  return (
    <div className={mobileOpen ? "min-h-screen lg:grid lg:grid-cols-[244px_minmax(0,1fr)]" : "grid min-h-screen lg:grid-cols-[244px_minmax(0,1fr)]"}>
      <div className="hidden lg:block">
        {user ? (
          <Sidebar
            navItems={navItems}
            user={user}
            onLogout={() => {
              void handleLogout();
            }}
            logoutPending={logout.isPending}
            className="sticky top-0 h-screen"
          />
        ) : null}
      </div>

      {mobileOpen && user ? (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-black/30"
            aria-label="Close menu"
            onClick={() => {
              setMobileOpen(false);
            }}
          />
          <div className="absolute left-0 top-0 h-full w-[min(280px,85vw)] shadow-xl">
            <Sidebar
              navItems={navItems}
              user={user}
            onNavigate={() => {
              setMobileOpen(false);
            }}
            onLogout={() => {
              void handleLogout();
            }}
              logoutPending={logout.isPending}
              className="h-full"
            />
          </div>
        </div>
      ) : null}

      <div className="min-w-0">
        <header className="sticky top-0 z-30 flex h-[65px] items-center justify-between gap-4 border-b border-edlab-line bg-white/80 px-5 backdrop-blur-2xl lg:h-[76px] lg:px-[38px]">
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="grid h-10 w-10 place-items-center rounded-lg border border-edlab-line text-edlab-ink lg:hidden"
              aria-label="Open menu"
              onClick={() => {
                setMobileOpen(true);
              }}
            >
              ☰
            </button>
            <p className="flex items-center gap-3 text-[13px] text-[#77818d]">
              Foundation
              <span className="hidden sm:inline">/</span>
              <span className="font-medium text-[#44505f]">{breadcrumb}</span>
            </p>
          </div>
          <div className="flex items-center gap-3">
            {user ? (
              <span className="hidden max-w-[220px] truncate text-sm text-edlab-muted md:inline">
                {user.email}
              </span>
            ) : null}
          </div>
        </header>

        <main id="main" className="mx-auto max-w-[1680px] px-5 py-8 lg:px-[38px] lg:pb-10">
          <RoleRouteGuard>
            <Outlet />
          </RoleRouteGuard>
        </main>
      </div>
    </div>
  );
}
